import { useMemo, useState } from 'react';
import { useIsMobile } from '../lib/useIsMobile';
import type { CalStatus, Content, InviteComponent } from '@zimbra-webif/sdk';
import { IconShare, IconTasks } from './icons';
import {
  dueOf,
  defaultComp,
  useCreateTask,
  useTask,
  useTaskFolders,
  useTasks,
  useToggleComplete,
  type TaskHit,
} from '../lib/tasks-queries';
import './tasks.css';

const SORT_OPTIONS: Array<{ v: string; label: string }> = [
  { v: 'taskDueAsc', label: 'Due ↑' },
  { v: 'taskDueDesc', label: 'Due ↓' },
  { v: 'taskStatusAsc', label: 'Status' },
  { v: 'taskPercCompletedAsc', label: '% complete' },
];

const PRIORITY_LABEL = (p: number | undefined): { cls: string; label: string } | null => {
  if (p === undefined) return null;
  // iCal PRIORITY: 1-4 = high, 5 = normal, 6-9 = low (RFC 5545).
  if (p >= 1 && p <= 4) return { cls: 'high', label: 'High' };
  if (p >= 6 && p <= 9) return { cls: 'low', label: 'Low' };
  return null;
};

function isComplete(status: CalStatus | undefined): boolean {
  return status === 'COMP';
}

const STATUS_LABEL: Record<string, string> = {
  NEED: 'Not started',
  INPR: 'In progress',
  COMP: 'Completed',
  WAITING: 'Waiting',
  DEFERRED: 'Deferred',
};

/** Strip a `Content | string` field down to a plain string. */
function asText(v: Content | string | undefined): string {
  if (v === undefined) return '';
  if (typeof v === 'string') return v;
  return v._content ?? '';
}

/* ------------------------------------------------------------------------- */
/* Due-date grouping                                                          */
/* ------------------------------------------------------------------------- */

interface DueGroup {
  key: string;
  label: string;
  tasks: TaskHit[];
}

function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function groupByDue(tasks: TaskHit[]): DueGroup[] {
  const today = startOfDay(Date.now());
  const tomorrow = today + 86_400_000;
  const weekEnd = today + 7 * 86_400_000;

  const buckets: Record<
    'overdue' | 'today' | 'tomorrow' | 'week' | 'later' | 'none' | 'done',
    DueGroup
  > = {
    overdue: { key: 'overdue', label: 'Overdue', tasks: [] },
    today: { key: 'today', label: 'Today', tasks: [] },
    tomorrow: { key: 'tomorrow', label: 'Tomorrow', tasks: [] },
    week: { key: 'week', label: 'This week', tasks: [] },
    later: { key: 'later', label: 'Later', tasks: [] },
    none: { key: 'none', label: 'No due date', tasks: [] },
    done: { key: 'done', label: 'Completed', tasks: [] },
  };

  for (const t of tasks) {
    if (isComplete(t.status)) {
      buckets.done.tasks.push(t);
      continue;
    }
    const due = dueOf(t);
    if (due === undefined) {
      buckets.none.tasks.push(t);
    } else if (due < today) {
      buckets.overdue.tasks.push(t);
    } else if (due < tomorrow) {
      buckets.today.tasks.push(t);
    } else if (due < tomorrow + 86_400_000) {
      buckets.tomorrow.tasks.push(t);
    } else if (due < weekEnd) {
      buckets.week.tasks.push(t);
    } else {
      buckets.later.tasks.push(t);
    }
  }

  return Object.values(buckets).filter((g) => g.tasks.length > 0);
}

function fmtDue(ms: number | undefined): string {
  if (ms === undefined) return '';
  return new Date(ms).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

/* ------------------------------------------------------------------------- */
/* Detail / edit panel                                                        */
/* ------------------------------------------------------------------------- */

function TaskDetail({ id }: { id: string }) {
  const taskQ = useTask(id);
  const comp: InviteComponent | undefined = useMemo(
    () => defaultComp(taskQ.data),
    [taskQ.data],
  );

  if (taskQ.isLoading) return <div className="tk-loading">Loading…</div>;
  if (!comp) return <div className="tk-empty">Task not found</div>;

  const pct = comp.percentComplete ?? (isComplete(comp.status) ? 100 : 0);
  const due = comp.e?.u;
  const prio = PRIORITY_LABEL(comp.priority);
  const notes = asText(comp.desc);

  return (
    <div className="tk-detail">
      <div className="tk-detail-hdr">
        <h2>{comp.name || '(no title)'}</h2>
        <div className="tk-detail-meta">
          <span className={`tk-status ${comp.status ?? 'NEED'}`}>
            {STATUS_LABEL[comp.status ?? 'NEED'] ?? comp.status}
          </span>
          {prio ? <span className={`tk-prio ${prio.cls}`}>{prio.label} priority</span> : null}
        </div>
      </div>

      <div className="tk-detail-body">
        <div className="tk-field">
          <span className="tk-field-label">Progress</span>
          <div className="tk-progress">
            <div className="tk-progress-bar" style={{ width: `${pct}%` }} />
          </div>
          <span className="tk-field-value">{pct}%</span>
        </div>

        <div className="tk-field">
          <span className="tk-field-label">Due</span>
          <span className="tk-field-value">
            {typeof due === 'number' ? fmtDue(due) : comp.e?.d ? comp.e.d : 'No due date'}
          </span>
        </div>

        {comp.loc ? (
          <div className="tk-field">
            <span className="tk-field-label">Location</span>
            <span className="tk-field-value">{comp.loc}</span>
          </div>
        ) : null}

        {notes ? (
          <div className="tk-notes">
            <span className="tk-field-label">Notes</span>
            <p>{notes}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* New-task inline composer                                                   */
/* ------------------------------------------------------------------------- */

function NewTaskForm({
  folderId,
  onClose,
}: {
  folderId: string | null;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [due, setDue] = useState('');
  const [priority, setPriority] = useState('5');
  const create = useCreateTask();

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    create.mutate(
      {
        name: trimmed,
        folderId: folderId ?? undefined,
        priority: Number(priority),
        due: due ? new Date(`${due}T00:00:00`).getTime() : undefined,
      },
      { onSuccess: onClose },
    );
  }

  return (
    <form className="tk-newform" onSubmit={submit}>
      <input
        className="tk-input"
        autoFocus
        placeholder="Task name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <div className="tk-newform-row">
        <input
          className="tk-input"
          type="date"
          value={due}
          onChange={(e) => setDue(e.target.value)}
        />
        <select
          className="tk-input"
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
        >
          <option value="1">High</option>
          <option value="5">Normal</option>
          <option value="9">Low</option>
        </select>
      </div>
      <div className="tk-newform-actions">
        <button type="button" className="tk-btn ghost" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="tk-btn" disabled={!name.trim() || create.isPending}>
          {create.isPending ? 'Adding…' : 'Add task'}
        </button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------------- */
/* Task row                                                                   */
/* ------------------------------------------------------------------------- */

function TaskRow({
  task,
  active,
  onOpen,
  onToggle,
  toggling,
}: {
  task: TaskHit;
  active: boolean;
  onOpen: () => void;
  onToggle: () => void;
  toggling: boolean;
}) {
  const done = isComplete(task.status);
  const pct = task.percentComplete ?? (done ? 100 : 0);
  const prio = PRIORITY_LABEL(task.priority);
  const due = dueOf(task);
  const overdue = !done && due !== undefined && due < startOfDay(Date.now());

  return (
    <div className={`tk-row${active ? ' active' : ''}${done ? ' done' : ''}`} onClick={onOpen}>
      <button
        type="button"
        className={`tk-check${done ? ' checked' : ''}`}
        aria-label={done ? 'Mark not complete' : 'Mark complete'}
        disabled={toggling}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
      >
        {done ? (
          <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden>
            <path
              d="M5 12.5l4.2 4.2L19 7"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : null}
      </button>

      <div className="tk-row-body">
        <div className="tk-row-top">
          {prio ? <span className={`tk-dot ${prio.cls}`} title={`${prio.label} priority`} /> : null}
          <span className="tk-name">{task.name || '(no title)'}</span>
          {due !== undefined ? (
            <span className={`tk-due${overdue ? ' overdue' : ''}`}>{fmtDue(due)}</span>
          ) : null}
        </div>
        {pct > 0 && pct < 100 ? (
          <div className="tk-progress slim">
            <div className="tk-progress-bar" style={{ width: `${pct}%` }} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* Screen                                                                     */
/* ------------------------------------------------------------------------- */

export function TasksScreen() {
  const [activeFolder, setActiveFolder] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState('taskDueAsc');
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);

  const foldersQ = useTaskFolders();
  const tasksQ = useTasks(activeFolder, sortBy);
  const toggle = useToggleComplete();

  const folders = foldersQ.data ?? [];
  const tasks = tasksQ.data ?? [];
  const groups = useMemo(() => groupByDue(tasks), [tasks]);

  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  return (
    <div
      className={`tk-shell${isMobile ? ' m-shell' : ''}${drawerOpen ? ' m-drawer' : ''}${
        isMobile && activeTaskId ? ' m-detail' : ''
      }`}
    >
      {isMobile && drawerOpen ? (
        <div className="m-backdrop" onClick={() => setDrawerOpen(false)} />
      ) : null}
      <aside className="pane tk-sidebar m-side">
        <div className="pane-hdr">Task Lists</div>
        <div className="pane-scroll">
          <div
            className={`tk-folder${activeFolder === null ? ' active' : ''}`}
            onClick={() => {
              setActiveFolder(null);
              setActiveTaskId(null);
              setDrawerOpen(false);
            }}
          >
            <span className="tk-fname">
              <IconTasks className="tk-list-glyph" />
              All Tasks
            </span>
          </div>
          {foldersQ.isLoading ? (
            <div className="tk-loading">Loading…</div>
          ) : (
            folders.map((f) => (
              <div
                key={f.id}
                className={`tk-folder${f.id === activeFolder ? ' active' : ''}`}
                style={{ paddingLeft: 12 + f.depth * 14 }}
                onClick={() => {
                  setActiveFolder(f.id);
                  setActiveTaskId(null);
                  setDrawerOpen(false);
                }}
              >
                <span className="tk-fname">
                  {f.shared ? <IconShare className="tk-list-glyph" /> : null}
                  {f.name}
                </span>
              </div>
            ))
          )}
        </div>
      </aside>

      <section className="pane tk-list m-list-pane">
        <div className="pane-hdr tk-listhdr">
          <button className="m-burger" aria-label="Task lists" onClick={() => setDrawerOpen(true)}>
            ☰
          </button>
          <span>{tasksQ.isFetching ? 'Refreshing…' : `${tasks.length} tasks`}</span>
          <div className="tk-listhdr-tools">
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              {SORT_OPTIONS.map((o) => (
                <option key={o.v} value={o.v}>
                  {o.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="tk-btn"
              onClick={() => setComposing((c) => !c)}
            >
              + New task
            </button>
          </div>
        </div>
        <div className="pane-scroll">
          {composing ? (
            <NewTaskForm folderId={activeFolder} onClose={() => setComposing(false)} />
          ) : null}

          {tasksQ.isLoading ? (
            <div className="tk-loading">Loading…</div>
          ) : tasks.length === 0 && !composing ? (
            <div className="tk-empty">No tasks</div>
          ) : (
            groups.map((g) => (
              <div key={g.key} className="tk-group">
                <div className="tk-group-hdr">{g.label}</div>
                {g.tasks.map((t) => (
                  <TaskRow
                    key={t.id ?? String(t.invId)}
                    task={t}
                    active={t.id === activeTaskId}
                    toggling={toggle.isPending}
                    onOpen={() => (t.id ? setActiveTaskId(t.id) : undefined)}
                    onToggle={() =>
                      t.id
                        ? toggle.mutate({ id: t.id, complete: !isComplete(t.status) })
                        : undefined
                    }
                  />
                ))}
              </div>
            ))
          )}
        </div>
      </section>

      <main className="pane tk-panel m-detail-pane">
        <button className="m-back" aria-label="Back" onClick={() => setActiveTaskId(null)}>
          ‹ Back
        </button>
        {activeTaskId ? (
          <TaskDetail id={activeTaskId} />
        ) : (
          <div className="tk-empty tk-empty-lg">
            <IconTasks className="tk-empty-icon" />
            <p>Select a task</p>
          </div>
        )}
      </main>
    </div>
  );
}
